import React, {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Typography } from '@galaxy-ds/core';
import style from './style.module.less';
import AttachIcon from './attach.svg?react';
import EmailIcon from './email.svg?react';
import { FileIcon } from '../../../components/files/file-icon';
import CrossCircleIcon from '../cross-circle.svg?react';
import { steps } from '../../../components/onboarding/steps';
import { useTranslation } from 'react-i18next';
/* the button behaviour is as follows: 
- request submitted
  request is gray, no hover and cursor is pointer
  intake is gray has hover, open tooltip
- request accepted
  request is green with tick icon
  intake is gray with hover, opens modal
- intake requested
  intake is green with same icon
- intake finished
  intake is green with tick icon
*/

interface ChatInputProps {
  onSendMessage: (args: { text: string; files: File[] }) => void;
  disabled?: boolean;
}

export const ChatInput: FC<ChatInputProps> = (props) => {
  const {
    onSendMessage,
    disabled,
  } = props;
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [files, setFiles] = React.useState<File[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>();
  const fileRef = useRef<HTMLInputElement>();

  // On paste of a file into the ref textarea, add it to the files
  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (items) {
        const newFiles = [];
        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === 'file') {
            const file = items[i].getAsFile();
            if (file) {
              newFiles.push(file);
            }
          }
        }
        if (newFiles.length) {
          setFiles([...files, ...newFiles]);
          e.preventDefault();
        }
      }
    },
    [files, setFiles]
  );

  const sendMessage = (e: MouseEvent | undefined = undefined) => {
    e?.preventDefault();
    e?.stopPropagation();

    onSendMessage({ text: message, files });
    setMessage('');
    setFiles([]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevents the default newline action
      sendMessage();
    }
  };

  const openFiles = useCallback(() => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  }, [fileRef]);

  useEffect(() => {
    // we want to find the root component and add a on drag over event to it
    const root = document.getElementById('root');
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const newFiles = e.dataTransfer.files;
      if (newFiles) {
        setFiles((f) => [...f, ...Array.from(newFiles)]);
      }
    };
    if (root) {
      root?.addEventListener('dragover', onDragOver);
      root?.addEventListener('drop', onDrop);
      return () => {
        root?.removeEventListener('dragover', onDragOver);
        root?.removeEventListener('drop', onDrop);
      };
    }
  }, []);

  const removeFileIndex = useCallback((index: number) => {
    setFiles((f) => f.filter((_, i) => i !== index));
  }, []);

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setFiles((f) => [...f, ...Array.from(e.target.files || [])]);
    },
    []
  );

  return (
    <div className={`${style.container} ${disabled ? style.disabled : ''}`}>
      <div className={style.wrapper}>
        <input
          ref={fileRef}
          type='file'
          className={style.fileInput}
          multiple
          onChange={onFileInputChange}
          disabled={disabled}
        />
        <div
          className={`${style.textBoxHolder}  ${
            files?.length ? style.withFiles : ''
          }`}
        >
          <textarea
            ref={textareaRef}
            className={style.inputTextarea}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={t('lawyer.input-placeholder')}
            autoFocus={true}
            onPaste={onPaste}
            disabled={disabled}
          />
          {files?.length ? (
            <div className={style.filesWrapper}>
              {files.map((f, index) => {
                return (
                  <div
                    key={f.name}
                    className={style.file}
                    onClick={() => removeFileIndex(index)}
                  >
                    <FileIcon
                      file={f}
                      className={style.fileIcon}
                      containerClassName={style.fileIconContainer}
                    />
                    <Typography variant='body1'>{f.name}</Typography>
                    <CrossCircleIcon className={style.crossIcon} />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className={style.divider} />
        <div className={style.actionBar}>
          <Button
            onClick={openFiles}
            variant='outlined'
            className={style.actionButton}
            disabled={disabled}
            data-onboarding-id={steps[1].refId}
            data-onboarding-position='top'
          >
            <Typography variant='button'>{t('lawyer.attach')}</Typography>
            <AttachIcon className={style.actionIcon} />
          </Button>
          <div className={style.floatRight}>
            <Button
              color='primary'
              onClick={sendMessage}
              variant='contained'
              className={style.primaryButton}
              disabled={
                (message?.length <= 0 && files?.length === 0) || disabled
              }
              data-onboarding-id={steps[4].refId}
              data-onboarding-position={'top'}
            >
              <Typography variant='button'>{t('lawyer.send')}</Typography>
              <EmailIcon className={style.actionIcon} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
